import { Component } from '@theme/component';

let resolvedSectionId = null;

function getSectionId() {
  if (resolvedSectionId) return resolvedSectionId;
  const sentinel = document.querySelector('[data-cart-drawer-recs-sentinel]');
  resolvedSectionId = sentinel?.dataset.sectionId ?? null;
  return resolvedSectionId;
}

class CartDrawerRecommendations extends Component {
  #productId = null;
  #hasLoaded = false;
  #list = null;
  #arrowsBound = false;
  #listResizeObserver = null;

  #onCartUpdate = (event) => {
    const { resource } = event.detail;
    const addedProductId = event.detail.data?.productId?.toString();
    const newProductId = addedProductId || resource?.items?.[0]?.product_id?.toString();
    const itemCount = resource?.item_count ?? event.detail.data?.itemCount ?? 1;

    if (itemCount === 0) {
      this.hidden = true;
      return;
    }

    if (newProductId && newProductId !== this.#productId) {
      this.#productId = newProductId;
      this.#load(newProductId);
    } else if (this.hidden && this.#hasLoaded) {
      // Same product as before (e.g. re-adding after emptying the cart) —
      // content is still valid, so just reveal it.
      this.hidden = false;
    }
  };

  connectedCallback() {
    super.connectedCallback();
    // On re-injection after morphSection, avoid re-fetching if already loaded
    const isReinsertion = this.#hasLoaded;
    if (!this.#productId) {
      this.#productId = this.dataset.productId;
    }
    this.#moveToScrollContainer();
    if (this.#productId && !isReinsertion) this.#load(this.#productId);
    document.addEventListener('cart:update', this.#onCartUpdate);
  }

  disconnectedCallback() {
    super.disconnectedCallback?.();
    document.removeEventListener('cart:update', this.#onCartUpdate);
    // morphSection just replaced cart-drawer__inner — re-inject on next frame.
    // Guard: a full-mode morph may have already inserted a fresh instance from
    // the server HTML and moved it into inner; if so, skip re-injection to
    // avoid ending up with two recommendation carousels.
    requestAnimationFrame(() => {
      const inner = document.querySelector('.cart-drawer__inner');
      if (inner?.querySelector('cart-drawer-recommendations')) return;
      this.#moveToScrollContainer();
    });
  }

  // Moves this element to the bottom of cart-drawer__inner so it sits
  // at the end of the cart's scroll area rather than outside it.
  #moveToScrollContainer() {
    const inner = document.querySelector('.cart-drawer__inner');
    if (inner && !inner.contains(this)) {
      inner.appendChild(this);
    }
  }

  async #load(productId) {
    // Stay hidden while (re)loading so the heading/arrows don't show ahead of content.
    this.hidden = true;

    const sectionId = getSectionId();
    if (!sectionId) {
      return;
    }
    const limit = this.dataset.limit || 10;
    try {
      const url = `/recommendations/products?section_id=${sectionId}&product_id=${productId}&limit=${limit}&intent=related`;
      const res = await fetch(url);
      if (!res.ok) {
        console.warn(`[cart-drawer-recommendations] fetch failed: ${res.status} ${res.statusText} — ${url}`);
        return;
      }
      const html = await res.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const list = doc.querySelector('.cart-drawer-rec-list');
      if (!list || !list.children.length) {
        console.warn('[cart-drawer-recommendations] no .cart-drawer-rec-list found in response', doc.body.innerHTML.slice(0, 200));
        return;
      }

      // Inject any section-scoped stylesheets included in the response
      doc.querySelectorAll('style[data-section-stylesheet]').forEach((style) => {
        const key = style.dataset.resource || style.textContent.slice(0, 40);
        if (!document.querySelector(`style[data-injected-key="${CSS.escape(key)}"]`)) {
          const clone = style.cloneNode(true);
          clone.dataset.injectedKey = key;
          document.head.appendChild(clone);
        }
      });

      const container = this.querySelector('.cart-drawer-recommendations__items, .cart-drawer-rec-list');
      if (container) container.replaceWith(list);
      this.#hasLoaded = true;
      this.#bindArrows(list);
      this.hidden = false;
    } catch {
      // stay hidden
    }
  }

  #bindArrows(list) {
    // The buttons are static (defined once in the template) but #load() —
    // and therefore #bindArrows() — can run again on every cart:update,
    // replacing `list` with a brand-new element each time. Only wire the
    // button click listeners once; otherwise every reload stacks another
    // listener on top of the old ones, each closing over a stale, detached
    // `list` — so a single click fires several competing scrolls.
    this.#list = list;

    const prevBtn = this.querySelector('.cart-drawer-recommendations__arrow--prev');
    const nextBtn = this.querySelector('.cart-drawer-recommendations__arrow--next');
    if (!prevBtn || !nextBtn) return;

    const update = () => {
      prevBtn.hidden = this.#list.scrollLeft <= 0;
      nextBtn.hidden = this.#list.scrollLeft + this.#list.clientWidth >= this.#list.scrollWidth - 1;
    };

    this.#listResizeObserver?.disconnect();
    this.#listResizeObserver = new ResizeObserver(update);
    this.#listResizeObserver.observe(list);
    list.addEventListener('scroll', update, { passive: true });
    update();

    if (this.#arrowsBound) return;
    this.#arrowsBound = true;

    // The response markup interleaves a <style data-shopify> tag before each
    // product-card, so list.children includes non-card nodes with no layout
    // box. Filter to elements that actually take up space in the row.
    const scrollBy = (direction) => {
      const cards = Array.from(this.#list.children).filter((el) => el.tagName !== 'STYLE');
      if (!cards.length) return;
      const listLeft = this.#list.getBoundingClientRect().left;
      const positions = cards.map(
        (card) => card.getBoundingClientRect().left - listLeft + this.#list.scrollLeft
      );
      let currentIndex = positions.findIndex((left) => left >= this.#list.scrollLeft - 1);
      if (currentIndex === -1) currentIndex = cards.length - 1;
      const targetIndex = Math.min(cards.length - 1, Math.max(0, currentIndex + direction));
      cards[targetIndex].scrollIntoView({ behavior: 'smooth', inline: 'start', block: 'nearest' });
    };

    prevBtn.addEventListener('click', () => scrollBy(-1));
    nextBtn.addEventListener('click', () => scrollBy(1));
  }
}

if (!customElements.get('cart-drawer-recommendations')) {
  customElements.define('cart-drawer-recommendations', CartDrawerRecommendations);
}
